import { useEffect, useMemo, useState } from 'react';
import { Loader2, Users } from 'lucide-react';
import { Modal, SearchBox } from '../DashboardPrimitives';
import { InlineLoader } from '../../loading';
import { customDashboardsAPI, rolesAPI } from '../../../services/api';
import type { CustomDashboard } from './types';

/**
 * Choose which roles may see a PUBLISHED dashboard.
 *
 * The server has enforced `audience_role_ids` since the persistence layer went in —
 * in `listDashboards` AND on the by-id fetch — but nothing ever set it, so every
 * published dashboard was visible to every member of the project. This is the
 * missing write side.
 *
 * Empty = everyone in the project, which stays the default: narrowing is a
 * deliberate act, and a dashboard that silently reached nobody because the picker
 * defaulted to "no roles" would be worse than one that reaches too many.
 *
 * ⚠️ Audience limits WHO SEES THE DASHBOARD. It does not change the rows inside it —
 * those are always narrowed to the viewer's own hierarchy by the runner. Restricting
 * the audience is therefore not a way to protect data, and the copy below says so.
 */

const errorMessage = (e: unknown): string =>
  (e as any)?.response?.data?.message || (e as any)?.message || 'Something went wrong.';

export default function AudienceModal({
  dashboard,
  projectId,
  onClose,
  onSaved,
}: {
  dashboard: CustomDashboard;
  projectId: number;
  onClose: () => void;
  onSaved: (next: CustomDashboard) => void;
}) {
  const [roles, setRoles] = useState<Array<{ id: string; name: string }>>([]);
  const [selected, setSelected] = useState<string[]>(dashboard.audience_role_ids ?? []);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    rolesAPI
      .getByProject(projectId)
      .then((res) => {
        if (!alive) return;
        const list = (res.data?.data ?? []) as Array<{ id: string | number; name: string }>;
        setRoles(list.map((r) => ({ id: String(r.id), name: r.name })));
      })
      .catch((e) => { if (alive) setError(errorMessage(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [projectId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? roles.filter((r) => r.name.toLowerCase().includes(q)) : roles;
  }, [roles, search]);

  const toggle = (id: string) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await customDashboardsAPI.update(dashboard.id, { audience_role_ids: selected });
      onSaved(res.data.dashboard as CustomDashboard);
      onClose();
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };

  const everyone = selected.length === 0;

  return (
    <Modal title="Who can see this dashboard" onClose={busy ? () => {} : onClose} maxWidth="max-w-lg">
      <div className="space-y-3">
        <p className="text-xs text-gray-500">
          Roles chosen here control who sees the dashboard. The numbers inside it are always
          limited to each viewer’s own team, whichever roles you pick.
        </p>

        <button
          type="button"
          onClick={() => setSelected([])}
          className={`flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition ${
            everyone
              ? 'border-primary-200 bg-primary-50/60 text-primary-800'
              : 'border-gray-200 text-gray-700 hover:bg-gray-50'
          }`}
        >
          <Users className="h-4 w-4 shrink-0" />
          <span className="flex-1">
            Everyone in this project
            <span className="block text-[11px] text-gray-500">The default.</span>
          </span>
          {everyone && <span className="text-xs font-semibold">Selected</span>}
        </button>

        <div>
          <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-gray-400">
            Or limit to specific roles
          </p>
          {roles.length > 6 && (
            <div className="mb-2">
              <SearchBox value={search} onChange={setSearch} placeholder="Search roles…" />
            </div>
          )}

          {loading ? (
            <p className="py-4 text-center text-xs text-gray-400"><InlineLoader label="Loading roles…" size="xs" className="flex justify-center" /></p>
          ) : roles.length === 0 ? (
            <p className="py-4 text-center text-xs text-gray-400">
              No roles found for this project.
            </p>
          ) : (
            <div className="max-h-64 space-y-1 overflow-y-auto rounded-lg border border-gray-100 p-1">
              {filtered.map((r) => (
                <label
                  key={r.id}
                  className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(r.id)}
                    onChange={() => toggle(r.id)}
                    className="h-3.5 w-3.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="flex-1 truncate">{r.name}</span>
                </label>
              ))}
              {filtered.length === 0 && (
                <p className="px-2 py-3 text-center text-xs text-gray-400">No roles match.</p>
              )}
            </div>
          )}
        </div>

        {dashboard.status !== 'published' && (
          <p className="rounded-lg border border-amber-100 bg-amber-50/60 px-3 py-2 text-[11px] text-amber-800">
            This dashboard is still a draft, so only you can see it regardless of the roles
            selected here. Publish it to apply this audience.
          </p>
        )}

        {error && <p className="text-xs text-rose-600">{error}</p>}

        <div className="flex items-center justify-between gap-3 border-t border-gray-100 pt-3">
          <p className="text-[11px] text-gray-400">
            {everyone
              ? 'Visible to everyone in this project.'
              : `Visible to ${selected.length} role${selected.length === 1 ? '' : 's'}.`}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="rounded-md border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void save()}
              disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-primary-700 disabled:opacity-50"
            >
              {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Save
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
